
import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface SharedNavigationProps {
  subLabel?: string;
  transparent?: boolean;
}

export default function SharedNavigation({ subLabel, transparent = true }: SharedNavigationProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileOpen]);

  const isHome = location.pathname === '/';
  const showWhiteText = transparent && !isScrolled;
  const textColor = showWhiteText ? 'text-white hover:text-gray-200' : 'text-gray-900 hover:text-gray-600';
  const dividerColor = showWhiteText ? 'text-white/50' : 'text-gray-300';

  const handleContactClick = useCallback(() => {
    setIsMobileOpen(false);
    if (isHome) {
      const el = document.getElementById('contact');
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    }
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  }, [isHome, navigate]);

  const handleWhatWeDoClick = useCallback(() => {
    setIsMobileOpen(false);
    if (isHome) {
      const el = document.getElementById('what-we-do');
      if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
    }
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('what-we-do');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  }, [isHome, navigate]);

  const iconColor = showWhiteText && !isMobileOpen ? 'text-white' : 'text-gray-900';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isMobileOpen ? 'bg-white shadow-sm' : transparent ? 'bg-transparent' : 'bg-white'
        }`}
      >
        <div className="flex items-center justify-between h-20 px-6 lg:px-32">
          {/* Left: Logo + sub-label */}
          <div className="flex items-center gap-6 lg:gap-8">
            <Link to="/" className="cursor-pointer" onClick={() => setIsMobileOpen(false)}>
              <img
                src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/ed07e655b2cd85eb693caada04c30b9e.png"
                alt="BITMOVES"
                className="h-7 lg:h-8 w-auto object-contain"
              />
            </Link>
            {subLabel && (
              <>
                <span className={`text-sm tracking-wider transition-colors ${isScrolled || isMobileOpen ? 'text-gray-300' : dividerColor}`}>/</span>
                <span className={`text-sm font-medium tracking-wide transition-colors ${isScrolled || isMobileOpen ? 'text-gray-900' : showWhiteText ? 'text-white' : 'text-gray-900'}`}>
                  {subLabel}
                </span>
              </>
            )}
          </div>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/" className={`text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap ${textColor}`}>Home</Link>
            <button type="button" onClick={handleWhatWeDoClick} className={`text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap ${textColor}`}>What We Do</button>
            <Link to="/mever" className={`text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap ${textColor}`}>MeVER</Link>
            <Link to="/mirari-echo" className={`text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap ${textColor}`}>Echo</Link>
            <Link to="/company" className={`text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap ${textColor}`}>Company</Link>
            <button type="button" onClick={handleContactClick} className={`text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap ${textColor}`}>Contact</button>
          </div>

          {/* Mobile hamburger button */}
          <button
            type="button"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors ${isScrolled || isMobileOpen ? 'text-gray-900' : iconColor}`}
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-5 flex flex-col justify-between">
              <span className={`block w-full h-0.5 rounded-full transition-all duration-300 ${isScrolled || isMobileOpen ? 'bg-gray-900' : showWhiteText ? 'bg-white' : 'bg-gray-900'} ${isMobileOpen ? 'rotate-45 translate-y-[9px]' : ''}`} />
              <span className={`block w-full h-0.5 rounded-full transition-all duration-300 ${isScrolled || isMobileOpen ? 'bg-gray-900' : showWhiteText ? 'bg-white' : 'bg-gray-900'} ${isMobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block w-full h-0.5 rounded-full transition-all duration-300 ${isScrolled || isMobileOpen ? 'bg-gray-900' : showWhiteText ? 'bg-white' : 'bg-gray-900'} ${isMobileOpen ? '-rotate-45 -translate-y-[9px]' : ''}`} />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <div
        className={`fixed inset-0 z-40 bg-white transition-all duration-500 lg:hidden ${
          isMobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className={`flex flex-col items-center justify-center h-full gap-1 transition-all duration-500 ${isMobileOpen ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0'}`}>
          <Link to="/" onClick={() => setIsMobileOpen(false)} className="text-2xl font-medium text-gray-900 hover:text-gray-500 transition-colors py-4 cursor-pointer">Home</Link>
          <button type="button" onClick={handleWhatWeDoClick} className="text-2xl font-medium text-gray-900 hover:text-gray-500 transition-colors py-4 cursor-pointer">What We Do</button>
          <Link to="/mever" onClick={() => setIsMobileOpen(false)} className="text-2xl font-medium text-gray-900 hover:text-gray-500 transition-colors py-4 cursor-pointer">MeVER</Link>
          <Link to="/mirari-echo" onClick={() => setIsMobileOpen(false)} className="text-2xl font-medium text-gray-900 hover:text-gray-500 transition-colors py-4 cursor-pointer">Echo</Link>
          <Link to="/company" onClick={() => setIsMobileOpen(false)} className="text-2xl font-medium text-gray-900 hover:text-gray-500 transition-colors py-4 cursor-pointer">Company</Link>
          <button type="button" onClick={handleContactClick} className="text-2xl font-medium text-gray-900 hover:text-gray-500 transition-colors py-4 cursor-pointer">Contact</button>

          <div className="mt-8 flex flex-col items-center gap-2 text-sm text-gray-400">
            <a href="mailto:inquiry@bitmoves.jp" className="hover:text-gray-600 transition-colors cursor-pointer">inquiry@bitmoves.jp</a>
            <p>Tokyo, Japan</p>
          </div>
        </div>
      </div>
    </>
  );
}
